import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  getDoc,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import { useAuthStore } from './auth'
import { writeAuditLog } from './auditLog'
import type { Incident } from '@/types'
import { IncidentStatus, TeamType, TeamStatus, PersonnelStatus, OverheadRole } from '@/types'

export const useIncidentStore = defineStore('incident', () => {
  const incidents = ref<Incident[]>([])
  const currentIncident = ref<Incident | null>(null)
  const loading = ref(false)

  let unsubscribeList: Unsubscribe | null = null
  let unsubscribeSingle: Unsubscribe | null = null

  function subscribeToIncidents(agencyId: string) {
    if (unsubscribeList) unsubscribeList()
    const q = query(collection(db, 'incidents'), where('agency_id', '==', agencyId))
    unsubscribeList = onSnapshot(q, (snap) => {
      incidents.value = snap.docs.map((d) => ({ incident_id: d.id, ...d.data() } as Incident))
    })
  }

  function subscribeToIncident(incidentId: string) {
    if (unsubscribeSingle) unsubscribeSingle()
    unsubscribeSingle = onSnapshot(doc(db, 'incidents', incidentId), (snap) => {
      currentIncident.value = snap.exists()
        ? ({ incident_id: snap.id, ...snap.data() } as Incident)
        : null
    })
  }

  async function createIncident(data: {
    incident_name: string
    incident_number: string | null
    contact_phone: string | null
    agency_id: string
    created_by: string
    mission_type?: string | null
    start_date?: string | null
    start_time?: string | null
    icp_location?: string | null
    requesting_agency_id?: string | null
    requesting_agency_name?: string | null
    situation_summary?: string | null
    status?: IncidentStatus
  }): Promise<{ incidentId: string; opId: string }> {
    const sync = useSyncStore()
    sync.pendingWrites++
    try {
      const payload = {
        ...data,
        incident_number: data.incident_number ?? null,
        contact_phone: data.contact_phone ?? null,
        mission_type: data.mission_type ?? null,
        start_date: data.start_date ?? null,
        start_time: data.start_time ?? null,
        icp_location: data.icp_location ?? null,
        requesting_agency_id: data.requesting_agency_id ?? null,
        requesting_agency_name: data.requesting_agency_name ?? null,
        situation_summary: data.situation_summary ?? null,
        status: data.status ?? IncidentStatus.Active,
        secondary_agency_logo: null,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      }
      const incidentRef = await addDoc(collection(db, 'incidents'), payload)
      const incidentId = incidentRef.id

      await writeAuditLog(incidentId, 'incident.created', 'incident', incidentId, null, payload)

      // Auto-populate secondary_agency_logo from requesting agency if available
      if (data.requesting_agency_id) {
        try {
          const reqAgencySnap = await getDoc(doc(db, 'agencies', data.requesting_agency_id))
          if (reqAgencySnap.exists()) {
            const reqAgencyLogo = (reqAgencySnap.data().agency_logo as string | null) ?? null
            if (reqAgencyLogo) {
              await updateDoc(incidentRef, { secondary_agency_logo: reqAgencyLogo })
            }
          }
        } catch {
          // best-effort — secondary logo is optional
        }
      }

      // Auto-create OP 1 with today's date
      const today = new Date().toLocaleDateString('en-CA') // YYYY-MM-DD
      const opPayload = {
        op_number: 1,
        date: today,
        created_by: data.created_by,
        created_at: serverTimestamp(),
      }
      const opRef = await addDoc(
        collection(db, 'incidents', incidentId, 'operational_periods'),
        opPayload,
      )

      await writeAuditLog(incidentId, 'op.created', 'op', opRef.id, null, { op_number: 1 })

      // Auto-create default Overhead team in OP 1 — starts In Assignment per checkin-flow spec
      const teamPayload = {
        team_identifier: 'Overhead',
        team_type: TeamType.Overhead,
        team_status: TeamStatus.OnScene,
        team_leader_personnel_id: null,
        op_id: opRef.id,
        created_at: serverTimestamp(),
      }
      const teamRef = await addDoc(
        collection(db, 'incidents', incidentId, 'operational_periods', opRef.id, 'teams'),
        teamPayload,
      )

      await writeAuditLog(incidentId, 'team.created', 'team', teamRef.id, null, {
        team_identifier: 'Overhead',
      })

      // Add the incident creator to the Overhead team as IC
      const userOpRef = await addDoc(
        collection(db, 'incidents', incidentId, 'operational_periods', opRef.id, 'user_ops'),
        {
          user_id: data.created_by,
          op_id: opRef.id,
          team_id: teamRef.id,
          active: true,
          role: OverheadRole.IC,
          created_at: serverTimestamp(),
        },
      )

      // Auto check-in the incident creator as IC on the Overhead team
      const authStore = useAuthStore()
      const profile = authStore.userProfile
      let agencyName = data.agency_id
      try {
        const agencySnap = await getDoc(doc(db, 'agencies', data.agency_id))
        if (agencySnap.exists()) agencyName = agencySnap.data().agency_name ?? data.agency_id
      } catch {
        // best-effort — fall back to agency_id string
      }
      const personnelPayload = {
        incident_id: incidentId,
        op_id: opRef.id,
        user_id: data.created_by,
        last: profile?.user_last_name ?? '',
        first: profile?.user_first_name ?? '',
        agency: agencyName,
        phone: profile?.phone ?? '',
        when_made: today,
        time_in: serverTimestamp(),
        time_out: null,
        resource: '',
        qualifications: {
          medical_qual: profile?.medical_qual ?? '',
          searcher_type: profile?.searcher_type ?? null,
          rope_qual: profile?.rope_qual ?? null,
          misc_quals: profile?.misc_quals ?? [],
        },
        category: OverheadRole.IC,
        status: PersonnelStatus.Assigned,
        team_id: teamRef.id,
        notes: '',
      }
      const personnelRef = await addDoc(
        collection(db, 'incidents', incidentId, 'operational_periods', opRef.id, 'personnel'),
        personnelPayload,
      )

      // Link personnel_id back to the user_op record
      await updateDoc(userOpRef, { personnel_id: personnelRef.id })

      await writeAuditLog(incidentId, 'personnel.checked_in', 'personnel', personnelRef.id, null, personnelPayload)

      return { incidentId, opId: opRef.id }
    } finally {
      sync.pendingWrites--
    }
  }

  async function updateIncident(id: string, data: Partial<Incident>) {
    const sync = useSyncStore()
    sync.pendingWrites++
    try {
      const patch = { ...data, updated_at: serverTimestamp() }
      await updateDoc(doc(db, 'incidents', id), patch)
      await writeAuditLog(id, 'incident.updated', 'incident', id, null, patch)
    } finally {
      sync.pendingWrites--
    }
  }

  async function closeIncident(id: string, outcome: 'found' | 'not_found' | 'suspended') {
    const sync = useSyncStore()
    sync.pendingWrites++
    try {
      const patch = {
        status: IncidentStatus.Closed,
        outcome,
        closed_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      }
      await updateDoc(doc(db, 'incidents', id), patch)
      await writeAuditLog(id, 'incident.closed', 'incident', id, null, patch)
    } finally {
      sync.pendingWrites--
    }
  }

  function unsubscribeAll() {
    if (unsubscribeList) { unsubscribeList(); unsubscribeList = null }
    if (unsubscribeSingle) { unsubscribeSingle(); unsubscribeSingle = null }
  }

  return {
    incidents,
    currentIncident,
    loading,
    subscribeToIncidents,
    subscribeToIncident,
    createIncident,
    updateIncident,
    closeIncident,
    unsubscribeAll,
  }
})
